import React from 'react';

const PrintSchedule = () => {
  return (
    <div className="min-h-screen bg-white text-black p-8 print:p-4">
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body { 
            font-family: Arial, sans-serif !important;
            font-size: 12pt !important;
            line-height: 1.4 !important;
          }
          .no-print { display: none !important; }
          .print-break { page-break-after: always; }
          h1 { font-size: 18pt !important; }
          h2 { font-size: 16pt !important; }
          h3 { font-size: 14pt !important; }
          .schedule-table { 
            width: 100% !important;
            border-collapse: collapse !important;
          }
          .schedule-table th,
          .schedule-table td {
            border: 1px solid #000 !important;
            padding: 8px !important;
            text-align: left !important;
          }
          .schedule-table th {
            background-color: #f0f0f0 !important;
            font-weight: bold !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .sunday-row {
            border-left: 4px solid #2196F3 !important;
            background-color: #e8f4f8 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .monday-row {
            border-left: 4px solid #4CAF50 !important;
            background-color: #f0f8e8 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .friday-row {
            border-left: 4px solid #FF9800 !important;
            background-color: #fff2e8 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .day-label {
            font-weight: bold !important;
            font-size: 14pt !important;
          }
          .sunday-label::before {
            content: "🔵 " !important;
          }
          .monday-label::before {
            content: "🟢 " !important;
          }
          .friday-label::before {
            content: "🟠 " !important;
          }
        }
        
        @media screen {
          .print-only { display: none; }
          .sunday-row {
            background-color: #e3f2fd;
          }
          .monday-row {
            background-color: #e8f5e8;
          }
          .friday-row {
            background-color: #fff3e0;
          }
        }
      `}</style>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Rueda de la Vida Dance Society</h1>
        <h2 className="text-2xl font-semibold mb-4">June 2025 Class Schedule</h2>
        <p className="text-lg">Vancouver, BC</p>
      </div>

      {/* Print Button (hidden when printing) */}
      <div className="no-print mb-8 text-center">
        <button 
          onClick={() => window.print()}
          className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors font-semibold"
        >
          🖨️ Print Schedule
        </button>
      </div>

      {/* Weekly Schedule Table */}
      <div className="mb-12">
        <h3 className="text-xl font-bold mb-4">Weekly Class Schedule</h3>
        
        {/* Legend */}
        <div className="mb-4 p-4 border border-gray-400 rounded bg-gray-50">
          <h4 className="font-bold mb-2">Visual Guide:</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-blue-600">🔵</span>
              <span>SUNDAY - Broadway Location</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">🟢</span>
              <span>MONDAY - 6th Ave Location</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-600">🟠</span>
              <span>FRIDAY - Sun Hop Park (FREE!)</span>
            </div>
          </div>
        </div>

        <table className="schedule-table w-full border-collapse border border-gray-800">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-800 p-3 font-bold">Day</th>
              <th className="border border-gray-800 p-3 font-bold">Time</th>
              <th className="border border-gray-800 p-3 font-bold">Class</th>
              <th className="border border-gray-800 p-3 font-bold">Level</th>
              <th className="border border-gray-800 p-3 font-bold">Location</th>
            </tr>
          </thead>
          <tbody>
            {/* Sunday Classes */}
            <tr className="sunday-row">
              <td className="border border-gray-800 p-3 font-semibold day-label sunday-label" rowSpan="3">SUNDAY</td>
              <td className="border border-gray-800 p-3">7:00 - 8:00 PM</td>
              <td className="border border-gray-800 p-3">Bachata</td>
              <td className="border border-gray-800 p-3">Beginner/Improver</td>
              <td className="border border-gray-800 p-3" rowSpan="3">345 W Broadway<br />Vancouver, BC V5Y 1P8</td>
            </tr>
            <tr className="sunday-row">
              <td className="border border-gray-800 p-3">8:00 - 9:00 PM</td>
              <td className="border border-gray-800 p-3">Salsa</td>
              <td className="border border-gray-800 p-3">Beginner/Improver</td>
            </tr>
            <tr className="sunday-row">
              <td className="border border-gray-800 p-3">9:00 - 10:00 PM</td>
              <td className="border border-gray-800 p-3">Social Dancing</td>
              <td className="border border-gray-800 p-3">All Levels</td>
            </tr>

            {/* Spacer Row */}
            <tr>
              <td colspan="5" className="border-0 p-1"></td>
            </tr>

            {/* Monday Classes */}
            <tr className="monday-row">
              <td className="border border-gray-800 p-3 font-semibold day-label monday-label" rowSpan="3">MONDAY</td>
              <td className="border border-gray-800 p-3">6:15 - 7:15 PM</td>
              <td className="border border-gray-800 p-3">Salsa</td>
              <td className="border border-gray-800 p-3">Intermediate</td>
              <td className="border border-gray-800 p-3" rowSpan="3">76 W 6th Ave<br />Vancouver, BC V5Y 1K1</td>
            </tr>
            <tr className="monday-row">
              <td className="border border-gray-800 p-3">7:15 - 8:15 PM</td>
              <td className="border border-gray-800 p-3">Bachata</td>
              <td className="border border-gray-800 p-3">Intermediate</td>
            </tr>
            <tr className="monday-row">
              <td className="border border-gray-800 p-3">8:15 - 9:15 PM</td>
              <td className="border border-gray-800 p-3">Salsa</td>
              <td className="border border-gray-800 p-3">Advanced</td>
            </tr>

            {/* Spacer Row */}
            <tr>
              <td colspan="5" className="border-0 p-1"></td>
            </tr>

            {/* Friday Classes */}
            <tr className="friday-row">
              <td className="border border-gray-800 p-3 font-semibold day-label friday-label" rowSpan="2">FRIDAY</td>
              <td className="border border-gray-800 p-3">7:00 - 7:30 PM</td>
              <td className="border border-gray-800 p-3">Salsa, Bachata, Merengue</td>
              <td className="border border-gray-800 p-3">Intro Lesson</td>
              <td className="border border-gray-800 p-3" rowSpan="2">Sun Hop Park<br />3333 Main St<br />Vancouver, BC<br /><strong>🎉 FREE EVENT!</strong></td>
            </tr>
            <tr className="friday-row">
              <td className="border border-gray-800 p-3">7:30 - 9:00 PM</td>
              <td className="border border-gray-800 p-3">Social Dancing</td>
              <td className="border border-gray-800 p-3">All Levels</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Important Information */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Important Information</h3>
        <div className="space-y-4">
          <div className="border border-gray-400 p-4 rounded">
            <h4 className="font-bold text-lg mb-2">🎉 FREE Friday Community Events!</h4>
            <p>All Friday classes at Sun Hop Park are <strong>completely FREE</strong> community events. No registration required - just show up and dance!</p>
          </div>
          
          <div className="border border-gray-400 p-4 rounded">
            <h4 className="font-bold text-lg mb-2">📍 Three Locations</h4>
            <ul className="space-y-2">
              <li><strong>Broadway Studio:</strong> 345 W Broadway, Vancouver (Sunday classes)</li>
              <li><strong>6th Ave Studio:</strong> 76 W 6th Ave, Vancouver (Monday classes)</li>
              <li><strong>Sun Hop Park:</strong> 3333 Main St, Vancouver (Friday FREE events)</li>
            </ul>
          </div>

          <div className="border border-gray-400 p-4 rounded">
            <h4 className="font-bold text-lg mb-2">💰 Class Pricing</h4>
            <ul className="space-y-1">
              <li>• Single Style (3 classes): $30</li>
              <li>• Both Styles (6 classes): $50</li>
              <li>• Drop-in Rate: $15 per class</li>
              <li>• <strong>Friday Events: FREE!</strong></li>
            </ul>
          </div>

          <div className="border border-gray-400 p-4 rounded">
            <h4 className="font-bold text-lg mb-2">ℹ️ Class Details</h4>
            <ul className="space-y-1">
              <li>• No partner required</li>
              <li>• All levels welcome</li>
              <li>• First class free trial available</li>
              <li>• Comfortable shoes recommended</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Contact Information</h3>
        <div className="border border-gray-400 p-4 rounded">
          <p className="mb-2"><strong>Email:</strong> info@ruedadance.org</p>
          <p className="mb-2"><strong>Website:</strong> www.ruedadance.org</p>
          <p className="mb-2"><strong>Instagram:</strong> @rueda.de.la.vida</p>
          <p><strong>Facebook:</strong> Salsa Dancing in Vancouver</p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-600 mt-8 pt-4 border-t border-gray-300">
        <p>Rueda de la Vida Dance Society • Vancouver, BC • June 2025</p>
        <p className="mt-2">Building vibrant communities through the joy of dance</p>
      </div>

      {/* Print-only date stamp */}
      <div className="print-only text-right text-xs text-gray-500 mt-4">
        Printed: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default PrintSchedule;